import moment from 'moment-timezone'
import selectn from 'selectn'
import { common } from '../../common'
import { isPropertyTruthy, getHostInfo } from '../../../../utils/common'
import metahelpers from './metahelpers'
const TWEET_CLASS = 'twitter-tweet', SCRIPT_TAG = '<script'

export const helpers = {
  /**
  * Get numeric period of the current innings
  * Earlier we used competition.details[0].period but now we are
  * using status.period
  *
  * @param  {Object} competition competition
  * @return {Number}         period of current innings
  */
  getCurrentPeriod(competition = {}) {
    return selectn('status.period',competition)
  },
  /**
   * [getMatchCards Get match cards]
   * @param  {Array}  cards [cards array]
   * @return {[type]}       [array]
   */
  getMatchCards(cards = []) {
    return cards
  },
  /**
   * Get frequently used flags for a match
   * @param  {Object} competition  competitions node from API
   * @param {Number}  ballsPerOver balls per over info from notes node
   * @return {Object}              flags {limitedOvers, updatesOnly etc}
   */
  getMatchFlags(competition = {}, ballsPerOver) {
    // By default, set all flags to undefined
    let flags = {
      liveAvailable : undefined,
      updatesOnly: undefined,
      limitedOvers: undefined,
      onWatchESPN: undefined,
      fastcastAvailable: undefined,
      isTestMatch: undefined,
      id: undefined,
      isBallPerOverSix: undefined
    }

    // Get an array of flags to pick
    let flagsToPick = Object.keys(flags)
    const numFlags = flagsToPick.length
    // Iterate over required flags and set their values
    for (var i = 0; i < numFlags; i++) {
      let currentFlag = flagsToPick[i]
      if (competition.hasOwnProperty(currentFlag)) {
        flags[currentFlag] = competition[currentFlag]
      }
    }

    //check if match is multiday
    const classEventType = selectn('class.eventType', competition) || ''
    flags.isTestMatch = (classEventType.toLowerCase() === 'test') ? true : false

    // Set default value of flag
    flags.international = undefined
    // Get class id from the feed
    let classId = selectn('class.internationalClassId', competition)
    /**
     * As of now, only below 3 class ids are considered as international
     * for fastcast implementation.
     * @type {Array}
     */
    const validIntlIds = [ '1', '2', '3' ]
    // Set flag to true only if value is found in above array
    if (validIntlIds.indexOf(classId) > -1) {
      flags.international = true
    }

    //This flag is for showing '6 balls per over' text, if the no. of balls per over not equals to 6
    flags.showBallPerOverSix = (typeof ballsPerOver !== undefined && ballsPerOver !== '' && ballsPerOver !== 6) ? true : false

    return flags
  },


  /**
   * Commentary team wants to embed tweets in the commentary text from CMS.
   * Twitter script embedded in the page breaking the content on the screen
   * Get htmlString without script tag if exists
   * Implemented only for tweets. Can be used for other social media like instagram, with additional checks, if needed
   * @param  {String}  htmlString String htmlString with/out embedded tweet
   * @return {String} htmlString - returns htmlString without <script if exists, otherwise input is returned as is
   *
   */
  removeScriptTag(htmlString) {
    if(htmlString.indexOf(TWEET_CLASS) !== -1 && htmlString.indexOf(SCRIPT_TAG) !== -1) {
      return  htmlString.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    }
    return htmlString
  },


  /**
   * Get information about all possible extra runs
   * @param  {Boolean}  forScorecard Is this for full scorecard or commentary
   * @return {Object} allExtras - map of extra name & abbreviation
   *                  abbreviations - array of `extra` abbreviations
   */
  getAllExtras(forScorecard = false) {
    // API has different property names for scorecard & commentary
    let byeKey = forScorecard ? 'byes' : 'byes'
    let wideKey = forScorecard ? 'wides' : 'wide'
    let noballKey = forScorecard ? 'noballs' : 'noBall'
    let legbyeKey = forScorecard ? 'legbyes' : 'legByes'

    // sequence in which extras should be displayed
    let allExtras = [
        { name : byeKey , abbreviation : 'b' } ,
        { name : legbyeKey, abbreviation : 'lb' },
        { name : noballKey, abbreviation : 'nb' },
        { name : wideKey , abbreviation : 'w' },
        { name : 'penalties', abbreviation : 'pen' }
    ]

    let abbreviations = []
    for (var i = 0; i < allExtras.length; i++) {
      let extra = allExtras[i]
      abbreviations.push(extra.abbreviation)
    }

    return { allExtras, abbreviations }
  },


  /**
   * Get total runs for an innings.
   * @param  {Object}   stats           stats object from all innings array
   * @param  {Object}   matchFlags      flags for this match
   * @param  {Boolean}  showOtherStats  true if Run Rate/Minutes should be shown
   * @return {String}   runs / wickets (overs, RR/minutes)
   */
  getInningsTotal(stats = {}, matchFlags = {}, showOtherStats = false) {
    // default variables
    let total = ''
    let runsWickets = ''
    let otherStats = []
    let otherStatsString = ''
    let ballsPerOverText = ''
    // set empty string as default value
    let val = ''
    //console.info('\ninput stats', stats)

    /**
     * Extract stats for this innings.
     * Set stats to empty strings only if not availble.
     * Do not use stats directly in a boolean operation since 0 is a valid stat
     * & falsy value.
     *
     * e.g. DO NOT use - let runs = selectn('brief.runs',stats) || ''
     */
    let runs = selectn('brief.runs',stats)
    runs = typeof runs !== 'undefined' ? runs : val

    let wickets = selectn('brief.wickets',stats)
    wickets = typeof wickets !== 'undefined' ? wickets : val

    let overs = selectn('brief.overs', stats)
    overs = typeof overs !== 'undefined' ? overs : val

    let rr = selectn('all.runRate.displayValue', stats)
    rr = typeof rr !== 'undefined' ? rr : val

    let duration = selectn('all.minutes.displayValue', stats)
    duration = typeof duration !== 'undefined' ? duration : val

    let description = selectn('brief.description',stats) || ''
    let isFollowOn = selectn('brief.followOn',stats) == 1 ? true : false

    //console.info('runs=%s wickets=%s overs=%s rr=%s minutes=%s', runs, wickets, overs, rr, duration)

    // Return default total if overs is an empty string
    if (overs === '') {
      return total
    }

    // Handle runs wickets
    if (runs !== '' && wickets !== '') {
      // Append all out (hide wickets) if innings description is all out
      if (description === 'all out') {
        runsWickets = [ runs, 'all out' ].join(' ')
      }
      /**
       * Display runs/wickets if number of wickets is not 10.
       * Do not use ===. In case typeof 10 changes in feed, front end code
       * should still work.
       */
      else if (wickets != '10') {
        runsWickets = [ runs, wickets ].join('/')
      }
      // default to runs
      else{
        runsWickets = runs
      }
    }

    // Handle declared & follow on cases
    if (description === 'declared') {
      runsWickets += 'd'
    }
    if (isFollowOn === true) {
      runsWickets += ' (f/o)'
    }

    // Format overs if present
    if (overs !== '') {
      otherStats.push(`${overs} Overs`)
    }

    // Display additional stats if showOtherStats option is true
    if (showOtherStats === true) {
      // Display run rate
      if (rr !== '') {
        //We are calculating run rate as 6 balls per over
        //This is for the matches, where balls per over is other than 6
        if (matchFlags.showBallPerOverSix === true) {
          ballsPerOverText = ' per 6 ball'
        }
        otherStats.push(`RR: ${rr}${ballsPerOverText}`)
      }
      // Display innings duration for test matches
      if (matchFlags.limitedOvers === false) {
        if (duration !== '') {
          otherStats.push(`${duration} minutes`)
        }
      }
    }

    if (otherStats.length !== 0) {
      otherStatsString = `(${otherStats.join(', ')})`
    }
    //console.info('otherStatsString', otherStatsString)
    total = `${runsWickets} ${otherStatsString}`
    //console.info('total', total)
    return total
  },


  /**
   * Get total extras with split of byes, wides, no balls, leg byes, penalties.
   * Also get the total number of extras
   *
   * @param  {Object} stats stats object from all innings array
   * @return {String}       Total (Nb, Nw, Nnb, Nlb, Npen)
   */
  getExtras(stats = {}) {
    // For final string = total extras (split string)
    let totalExtras = []
    let totalExtrasString = ''
    // For split string Nb, Nw, Nn, Nlb
    let extraSplit = []
    let extraSplitString = ''

    // Get count of number of extras given
    let count = selectn('all.extras.displayValue', stats)
    if (typeof count !== 'undefined') {
      totalExtras.push(count)
    }

    let extraDetails = helpers.getAllExtras(true) || {}
    let whichExtras = extraDetails.allExtras
    const numExtras = whichExtras.length
    // Iterate over required extra stats and get their values
    for (var i = 0; i < numExtras; i++) {
      let extra = whichExtras[i]
      let extraValue = selectn(`all[${extra.name}].displayValue`, stats)
      //console.info('%s = %s', extra.name, extraValue)
      if (typeof extraValue !== 'undefined' && extraValue != 0) {
        extraSplit.push(`${extra.abbreviation} ${extraValue}`)
      }
    }

    // Join the split only if any extras are found
    if (extraSplit.length !== 0) {
      extraSplitString = `(${extraSplit.join(', ')})`
      totalExtras.push(extraSplitString)
    }
    // Join the total string only if total count or split count is found
    if (totalExtras.length !== 0) {
      totalExtrasString = totalExtras.join(' ')
    }
    //console.info('totalExtrasString', totalExtrasString)
    return totalExtrasString
  },


  /**
   * Get title(s) of innings.
   * For Limited over matches, get team name Innings
   * For Test matches, get team name 1st/2nd Innings
   *
   * @param  {Object} competitor an object from competitions.competitors array
   * @param  {Object} matchFlags flags for this match
   * @param  {Object} score      linescores node
   * @return {String}            title for the innings
   */
  getInningsTitle(competitor, matchFlags, score) {
    let innText = 'Innings'
    let innNum = ''
    let delimiter = ' - '
    // Default titles to return
    let titles = {
      full : 'Innings',
      brief : 'Innings'
    }

    let teamDisplayName = selectn('team.displayName', competitor)
    let teamAbbreviation = selectn('team.abbreviation', competitor)
    // Use 1st as default for test matches
    if (matchFlags.limitedOvers === false) {
      innNum = '1st'
    }
    // Use this to construct full string - team name nth Innings
    let inningsTitle = [ teamDisplayName ]
    // Use this to construct brief string - team aabreviation nth
    let inningsAbbrev = [ teamAbbreviation ]

    let period = parseInt(score.period, 10)

    // Build all innings array only if period is positive integer and team is batting
    if (score.isBatting === true && period >= 0) {
      if (period >= 3) {
        innNum = '2nd'
      }
      // Do not append if inn num is empty string (for limited over matches)
      if (innNum !== '') {
        inningsAbbrev.push(innNum)
        inningsTitle.push(innNum)
      }
      inningsTitle.push(innText)
      // Full innings title
      titles = {
        full: inningsTitle.join(delimiter),
        brief: inningsAbbrev.join(delimiter)
      }
    }
    return titles
  },

  /**
   * Get active batsmen & bowlers.
   *
   * @param  {Array}  activePlayers  Array of currently active players
   * @param  {String} role           batsmen/bowlers
   * @param {String}  currentPeriod  ongoing innings
   * @return {Array}                 Active batmen/bowlers
   */
  getActivePlayersByRole(activePlayers = [], role = '', currentPeriod = '') {
    // Map to lookup activeName keys of batsmen and bowlers
    let roleToName = {
      batting : [ 'striker', 'non-striker' ],
      bowling : [ 'current bowler', 'previous bowler' ]
    }

    // Map required for allInnings array lookup
    let periodToInnings = {
      '1' : 0,
      '2' : 1,
      '3' : 2,
      '4' : 3
    }

    // Default players to return
    let playersByRole = []

    // Return players as is if role is not batsmen/bowlers or current period
    // is not found in periodToInnings
    if (roleToName.hasOwnProperty(role) === false ||
      periodToInnings.hasOwnProperty(currentPeriod) === false) {
      return playersByRole
    }

    // Valid strings for players
    let lookup = roleToName[role]

    let numActivePlayers =  activePlayers.length
    /**
     * Loop over active players. Clone the player object so that original object
     * is not modified. Extract the stats for the live/ongoing innings by
     * finding out the current innings. If the period property in extracted
     * stats matches with current period, assign all stats, bowling/batting
     * stats to combinedStats object.
     */
    for (var i = 0; i < numActivePlayers; i++) {
      let player = activePlayers[i]
      // continue if activeName is not found
      if (player.hasOwnProperty('activeName') === false) {
        continue
      }
      // continue if activeName value is not found in roleToName
      let activeName = player.activeName
      if (lookup.indexOf(activeName) === -1) {
        continue
      }
      // This is valid active player
      // Clone the object so that original object is not modified
      let currentPlayer = Object.assign({}, player)
      let combinedStats = {}
      // array indexes are period - 1
      let idx = periodToInnings[currentPeriod]
      // try to get current innings stats
      let currentInningStats = selectn(`linescores[${idx}]`, currentPlayer)
      // continue if stats are not found
      if (typeof currentInningStats === 'undefined') {
        continue
      }
      // Continue only if period in stats matches with current period
      if (currentInningStats.period == currentPeriod) {
        let stats = currentInningStats.statistics
        // lookup batting/bowling stats
        if (stats.hasOwnProperty(role) === true) {
          combinedStats.brief = stats[role]
        }
        // lookup detailed stats
        if (typeof selectn('categories[0].stats', stats) != 'undefined') {
          combinedStats.all = common.createStatsMap(stats.categories[0].stats)
        }
        //
        if (currentPlayer.stats) {
          combinedStats.career = common.createStatsMap(currentPlayer.stats)
        }

        // populate a new property combinedStats for easier lookup
        currentPlayer.combinedStats = combinedStats
        playersByRole.push(currentPlayer)
      }
    }

    // Custom comparator - to sort active players by 'striker'/'current bowler'
    let preferredRole = lookup[0]
    playersByRole.sort(function (p1,p2) {
      // p1 should be placed before p2
      if (p1.activeName === preferredRole) {
        return -1
      }
      // p1 should be placed after p2
      if (p2.activeName === preferredRole) {
        return 1
      }
      // no change
      return 0
    })

    //console.info('playersByRole', playersByRole)
    return playersByRole
  },
  /**
   * Get Player vs Player stats. Used in This Bowler & Last N overs thead
   * in batsman table
   *
   * @param  {Object} pvp pvp data from current batsmen
   * @return {String}     String in format runs (n balls)
   */
  getPVP(pvp = {}) {
    // default return string
    let pvpString = ''
    // Return empty string if data not found
    if (typeof pvp === 'undefined') {
      return pvpString
    }
    // container object to create pvp data
    let pvpItems = []
    let pvpRuns = pvp.runs
    let pvpBalls = pvp.balls
    // Push runs and balls to array if not empty
    if (typeof pvpRuns !== 'undefined') {
      pvpItems.push(pvpRuns)
    }
    if (typeof pvpBalls !== 'undefined') {
      pvpItems.push(`(${pvpBalls}b)`)
    }
    // Create string if array is not empty
    if (pvpItems.length !== 0) {
      pvpString = pvpItems.join('')
    }
    return pvpString || '-'
  },

  /**
   * Get standings link details
   * @param  {Object} params      Router params
   * @param  {Object} query       Router query
   * @return {Object}             Link title and href
   */
  getStandingsLink( params = {}, query = {} ) {
    const leagueId = params.leagueId || params.league
    const link = {
      title: 'Full Table',
      trackingName: common.createTrackingName([ params.sport, 'game', params.pageType, 'fullstandings' ])
    }

    if(leagueId) {
      link.href = common.createHref({ query, ...params, pageType: 'table' })
    }

    return link
  },

  /**
   * get Debutants
   * @param  {Array}  debutsNode debut node
   * @param  {Object} params router params
   * @return {Array}             list of debuts
   */
  getDebutants(debutsNode = [], params={}) {
    // default return debutants list array
    let debutants = []
    // Iterate over debutsNode and get each rosters
    debutsNode.map((d={}) => {
      // Iterate over rosters and get the information on each debutans
      const debutRoster = d.roster || []
      debutRoster.map((e={}) => {
        let dimg = selectn('athlete.flag.href', e) || ''
        // Append debut players information to debutants list array
        debutants.push({
          name: selectn('athlete.displayName', e) || '',
          img: '',
          href: common.extractUrl(selectn('athlete', e), 'Player Profile', 'desktop'),
          trackingName: common.createTrackingName([ params.sport, 'game', params.pageType, 'player' ]),
          imgicon: common.resizeCombinerLogo(dimg, { w:25, h:25 } )
        })
      })
    })
    return debutants
  },


  /**
   * Get displayable value for a ball. This is used in both recent overs and
   * bbb commentary.
   *
   * @param  {Object} item commentary item
   * @return {String}      value for that ball ({0 to 6}lb,nb,b,wd)
   */
  getBallValue(item = {}) {

    // Set default value to runs scored on this ball
    let eventValue = item.scoreValue

    const dismissal = selectn('dismissal.dismissal', item) ? true : false
    // If there is wicket, set value to W and return.
    if (dismissal === true) {
      eventValue = 'W'
      //console.info('getBallValue', eventValue)
      return eventValue
    }

    // Get data from over object which has extra runs information
    let over = item.over || {}

    // Extract lookup for extra runs
    let extraDetails = helpers.getAllExtras() || {}
    let whichExtras = extraDetails.allExtras
    const numExtras = whichExtras.length

    /**
     * Iterate over required extra stats, check if current ball has an extra
     * run assoicated with it. If it has, show in format of
     * number of runs + extra abbreviation e.g. 1w, 1nb, 1wd
     */
    for (var i = 0; i < numExtras; i++) {
      let currentExtra = whichExtras[i] || {}
      let extraName = currentExtra.name
      let extraAbbr = currentExtra.abbreviation
      if (over.hasOwnProperty(extraName)) {
        let extraRuns = over[extraName]
        if (extraRuns > 0) {
          //eventValue = `${extraRuns}${extraAbbr}`
          eventValue = `${eventValue}${extraAbbr}`
        }
      }
    }
    //console.info('getBallValue', eventValue)
    return eventValue
  },


  /**
   * split details into recent n overs
   * @param  {Array}  details - details node
   * @param {Number}  currentPeriod - current period being played
   * @return {Array}          overs
   */
  getRecentOvers(details = [], currentPeriod) {
    let currentOver = 0
    let recentOvers = []
    let over = []

    details.map((detail) => {
      let currentBall = ''

      // skip if previous innings
      if (detail.period !== currentPeriod) {
        return
      }

      if(currentOver !== detail.over.number) {
        currentOver = detail.over.number
        over = []
        recentOvers.push(over)
      }
      currentBall = helpers.getBallValue(detail)
      over.push(currentBall)
    })
    //console.info('recentOvers', recentOvers)
    return recentOvers
  },


  /**
   * Get player roles
   * @param  {Object} p player details object
   * @return {String}   captain or wicket keeper or both
   */
  getPlayerRoles(p = {}) {
    // Add provision for multiple roles for a player (e.g. captain, keeper)
    let playerRoles = []
    let playerRolesString = ''

    // check if keeper is captain
    if (p.captain) {
      playerRoles.push('(c)')
    }

    /**
     * Check if player is wicket keeper
     * Wicket keeper position id is present in either player.keeper or
     * player.position.id so check in both.
     */
    // Valid wicket keeper position ids
    let validKeeperIds = [ 'WK', 'WBT' ]
    // Get position id for the player
    let positionId = selectn('position.id', p) || ''
    // Check for both properties
    if (validKeeperIds.indexOf(positionId) > -1 ||
      isPropertyTruthy(p, 'keeper')) {
      playerRoles.push('&dagger;')
    }
    // Concatenate roles if any
    if (playerRoles.length !== 0) {
      playerRolesString = playerRoles.join(' ')
    }
    //console.info('playerRolesString', playerRolesString)
    return playerRolesString
  },


  /**
   * Get details of other team which will bat in second innings
   * @param  {Object} otherTeam details of other team
   * @return {Object} details   object with props name & players.
   *                            Name is name of team batting second
   *                            players is an array containing all players
   */
  getOtherTeamDetails(otherTeam = {}) {
    //console.info('otherTeam', otherTeam)
    // Default data structure to return
    let details = {
      name : '',
      players: []
    }
    // If other team does not have team roster available, return default
    if (otherTeam.hasOwnProperty('roster') === false) {
      return details
    }
    // Extract name of the team which will bat in second innings
    let name = selectn('team.displayName', otherTeam) || ''
    // Get the lineup
    let lineup = otherTeam.roster || []

    let numOtherPlayers = lineup.length
    // prep return data structure
    let players = []
    // Iterate over players and get the information on each player
    for (var i = 0; i < numOtherPlayers; i++) {
      let player = lineup[i]
      let athleteRole = selectn('position.name',player)
      if (typeof athleteRole === 'undefined' || athleteRole === 'Unknown') {
        athleteRole = ''
      }
      let info= {
        name: selectn('athlete.displayName',player),
        athleteRole: athleteRole,
        href: common.extractUrl(player.athlete, 'Player Profile', 'desktop'),
        captain: player.captain,
        roles : helpers.getPlayerRoles(player)
      }
      // Append to players
      players.push(info)
    }
    details = { name, players }
    //console.info('getOtherTeamDetails', details)
    return details
  },

  /**
   * get ordered innings
   * @param  {Array}  rosters Rosters node
   * @param  {Array}  allInnings  details of all innings
   * @param  {Number} currentPeriod current innings
   * @param  {String} gameState  game state
   * @param  {Object} matchFlags    flags for this match
   * @param  {Object} params  contains params for the page like pagename, sport etc
   * @param  {Object} device gives device info
   * @param {Number} stateTypeId gives state type Id which indicates match state. 5 if it is abandoned
   * @return {Object}         ordered innings
   */
  getOrderedInnings(rosters = [], allInnings, currentPeriod, gameState, matchFlags, params, device = {}, stateTypeId = 1) {
    const teamA = selectn('[0]', rosters) || []
    const teamB = selectn('[1]', rosters) || []
    let showSquads = false
    //console.info('teamA, teamB', teamA, teamB)
    /**
     * Placeholders to get details for other team i.e. team which will bat in
     * second innings. These details should be populated only when first
     * innings is live
     */
    let otherTeam = {}
    let otherTeamDetails = {}
    //console.info('allInnings', allInnings)
    //console.info('\ncurrentPeriod, gameState', currentPeriod, gameState)
    // Remaining batsmen text should be set to did not bat / yet to bat
    let remainingBatsmen
    let notBatted = 'Did not bat'
    let notBattedYet = 'Yet to bat'
    // If match is over, show did not bat
    if (gameState === 'final') {
      remainingBatsmen = notBatted
    }

    const battingStatMap = {
      runs: { text: 'R', index: 0 },
      ballsFaced: { text: 'B', index: 1 },
      minutes: { text: 'M', index: 2 },
      fours: { text: '4s', index: 3 },
      sixes: { text: '6s', index: 4 },
      strikeRate: { text: 'SR', index: 5 }
    }

    const bowlingStatMap = {
      overs: { text: 'O', index: 0 },
      maidens: { text: 'M', index: 1 },
      conceded: { text: 'R', index: 2 },
      wickets: { text: 'W', index: 3 },
      economyRate: { text: 'Econ', index: 4 }
    }
    // 0s, 4s and 6s should be shown only for limited over matches.
    if (matchFlags.limitedOvers === true) {
      bowlingStatMap.dots = { text: '0s', index: 5 }
      bowlingStatMap.foursConceded = { text: '4s', index: 6 }
      bowlingStatMap.sixesConceded = { text: '6s', index: 7 }
    }

    let bowlingStatIdx = matchFlags.limitedOvers === true ? 8 : 5
    if(device.desktop == true) {
      bowlingStatMap.wides = { text: 'WD', index: bowlingStatIdx },
      bowlingStatMap.noballs = { text: 'NB', index: bowlingStatIdx+1 }
    }
    const innings = {};

    [ teamA, teamB ].map((team) => {
      // Cache current team's ID
      let currentTeamId = selectn('team.id', team)
      let roster = team.roster || []
      roster.map((roster = { linescores:[] }) => {
        //console.info('team.roster', roster)
        let isStarter = roster.starter
        //console.info('athlete, starter', roster.athlete.displayName, isStarter)
        let linescoresLen = roster.linescores.length || 0
        roster.linescores.map((l) => {
          let inning = l.period
          let idx = inning-1
          // Get id of the team which was batting in this innings
          let battingTeamId = selectn(`[${idx}].teamId`, allInnings)
          /**
           * If currentPeriod is 1, other team is yet to Bat, so show full
           * squad. If batting team id is not equal to current iterating team,
           * we are in second team so equate that to other team.
           */
          //console.info('battingTeamId, currentTeamId', battingTeamId, currentTeamId)
          if (currentPeriod == 1 && battingTeamId !== currentTeamId) {
            otherTeam = team
          }
          /**
           * If innings is 1 and match state is abandoned (5) then
           *  other team player details need to be shown. Same logic as innings break
           */
          if( stateTypeId == 5 && linescoresLen == 1 && battingTeamId !== currentTeamId ) {
            otherTeam = team
          }
          // Get title for the innings
          let inningsTitle = selectn(`[${idx}].title`, allInnings) || 'Innings'
          // Get stats for this innings
          let inningStats = selectn(`[${idx}].stats`, allInnings) || {}
          // Get fall of wickets for this innings
          let inningFows = selectn(`[${idx}].fows`, allInnings) || []
          //console.info('inning, idx, battingTeamId', inning, idx, battingTeamId)
          let inningStatus = selectn(`[${idx}].inningStatus`, allInnings) || ''
          innings[inning] = innings[inning] || {}
          // Init properties for the final object
          innings[inning].title = inningsTitle
          innings[inning].inningStatus = inningStatus
          innings[inning].isCurrent = selectn(`[${idx}].isCurrent`, allInnings) || false
          innings[inning]['batsmen'] = innings[inning]['batsmen'] || []
          innings[inning]['bowlers'] = innings[inning]['bowlers'] || []
          innings[inning]['didNotBat'] = innings[inning]['didNotBat'] || []
          innings[inning]['absent'] = innings[inning]['absent'] || []
          const statistics = selectn('statistics', l)
          const battingOrder = selectn('batting.order', statistics)
          const bowlingOrder = selectn('bowling.order', statistics)
          const statsNode = selectn('categories[0].stats', statistics)
          // Create a map of stats for easier lookup
          const namedStats = common.createStatsMap(statsNode)
          // Array index is period/inning-1
          const currentLinescores = selectn(`linescores[${idx}]`, roster) || {}
          // hasVideoId flag checks if a player has video associated
          const player = {
            name: selectn('athlete.displayName', roster),
            href: common.extractUrl(roster.athlete, 'Player Profile', 'desktop'),
            captain: roster.captain,
            roles : helpers.getPlayerRoles(roster),
            trackingName: params.sport && params.pageType ? common.createTrackingName([ params.sport, 'game', params.pageType, 'player' ]) : '',
            hasVideoId: false
          }
          // Check if this player has video id associated with it
          const hasVideoId = typeof selectn('media.id', currentLinescores) !== 'undefined' ? true : false
          // If video is available, set video details on player object & update flag
          if (hasVideoId === true) {
            player.hasVideoId = true
            player.videoId = selectn('media.id', currentLinescores)
            player.video = currentLinescores.media || {}
          }

          //Check if any player absent hurt
          const absentHurt = selectn('absentDescription.displayValue', namedStats) == 'absent' ? true : false
          if(battingOrder) {
            player.stats = common.filterStatsByNames(statsNode, battingStatMap)
            // Check if player is not out
            let isNotOut = selectn('statistics.batting.outDetails.dismissalCard', currentLinescores) === 'not out' ? true : false
            // Add flag on player object to detect not out
            player.isNotOut = isNotOut
            //console.info('player %s is not out? %s', player.name, isNotOut)
            player.commentary = selectn('statistics.batting.outDetails.details.text', currentLinescores) || ''
            player.runningScore = selectn('statistics.batting.outDetails.details.innings', currentLinescores) || {}
            player.runningOver = selectn('statistics.batting.outDetails.details.over', currentLinescores) || {}
            // Pick how out dismissal string as is from feed
            player.shortText = selectn('statistics.batting.outDetails.shortText', currentLinescores) || ''

            // Array index is order-1
            innings[inning]['batsmen'][battingOrder-1] = player
          }

          if(absentHurt === true) {
            player.shortText = 'absent hurt'
            innings[inning]['absent'].push(player)
          }

          // Has this player batted in this innings?
          let hasBatted = selectn('batted.displayValue', namedStats) == '1' ? true : false
          //console.info('%s has batted in innings #%s?', player.name, inning, hasBatted)
          /**
           * Populate did not bat array only if current team id (iterator)
           * matches with team that has batted in this innings and
           * player's hasBatted property is false and
           * player has started the match i.e. he is not a substitute
           */
          if (currentTeamId == battingTeamId && hasBatted === false && isStarter === true && absentHurt === false) {
            innings[inning]['didNotBat'].push(player)
          }
          if(bowlingOrder) {
            player.stats = common.filterStatsByNames(statsNode, bowlingStatMap)
            innings[inning]['bowlers'][bowlingOrder-1] = player
          }
          if(currentTeamId == battingTeamId) {
            innings[inning].total = helpers.getInningsTotal(inningStats, matchFlags, true)
            innings[inning].extras = helpers.getExtras(inningStats)
            innings[inning].fows = inningFows
          }
          //hides extras, total row for forfeited innings
          if(inningStatus === 'forfeit') {
            innings[inning].total = ''
            innings[inning].extras = ''
            innings[inning].fows = ''
          }

          /**
           * Set text for remaining batsmen in case match state is not final.
           * If live innings, display yet to bat.
           * For older innings, display did not bat
           */
          if (gameState !== 'final') {
            remainingBatsmen = inning === currentPeriod ? notBattedYet : notBatted
            //console.info('inning=%s currentPeriod=%s remainingBatsmen=%s' , inning, currentPeriod, remainingBatsmen)
          }
          innings[inning].remainingBatsmen = remainingBatsmen
          innings[inning].headerInfo = helpers.inningsHeaderInfo(allInnings[idx], inning, matchFlags) || ''

        })

      })

    })
    //following code will checkif any of the stats is not available
    // for all the players to avoid empty column in the stats table
    for(let key in innings) {
      let batsmen = selectn(`[${key}].batsmen`,innings)
      let bowlers = selectn(`[${key}].bowlers`,innings)
      innings[key].batsmenKeys = helpers.checkIfStatsAreValid(battingStatMap, batsmen) || {}
      innings[key].bowlerKeys = helpers.checkIfStatsAreValid(bowlingStatMap, bowlers) || {}
    }

    if (Object.keys(innings).length === 0) {
      showSquads = true
    }
    //console.info('innings', innings)
    // Get details of the other team
    otherTeamDetails = helpers.getOtherTeamDetails(otherTeam)
    return { innings, otherTeamDetails, showSquads }
  },
  /**
  * Innings header info text
  * @param  {Object}  inning innings details as object
  * @param  {Number}  idx innings number
  * @param  {Object}  matchFlags match flags details
  * @return {String}  header info
  */
  inningsHeaderInfo( inning, idx, matchFlags ) {
    let inningsHeaderInfo = '',
      headInfo1 = {},
      headInfo2 = {},
      headInfo3 = {},
      headInfo4 = {},
      headInfo1Text = '',
      headInfo2Text = '',
      headInfo3Text = '',
      headInfo4Text = '',
      oversLabel = '',
      inningStatus = '',
      inningStatusDesc = ''

    if (matchFlags.limitedOvers === true) {
      headInfo1.value = selectn('stats.all.overLimit.displayValue', inning) || ''
      headInfo2.value = selectn('stats.all.target.displayValue', inning) || ''
      if (!headInfo1.value) {
        return inningsHeaderInfo
      }

      headInfo1.description = selectn('stats.all.overs.displayName', inning) || ''
      headInfo2.description = selectn('stats.all.target.displayName', inning) || ''
      headInfo1.value = headInfo1.value ? parseFloat(headInfo1.value) : ''
      headInfo2.value = headInfo2.value ? parseInt(headInfo2.value, 10) : ''

      oversLabel = (headInfo2.value < 2) ? 'run' : 'runs'

      headInfo1Text = `(${headInfo1.value} ${headInfo1.description} maximum)`
      headInfo1Text = headInfo1Text.toLowerCase()

      if(headInfo1.value && headInfo2.value) {
        headInfo2Text = `(${headInfo2.description}: ${headInfo2.value} ${oversLabel} from ${headInfo1.value} ${headInfo1.description})`
        headInfo2Text = headInfo2Text.toLowerCase()
      }

    }else if (matchFlags.isTestMatch === true) {

      headInfo3.value = selectn('stats.brief.followOn', inning)
      headInfo4.value = selectn('stats.all.target.displayValue', inning)
      headInfo4.value = headInfo4.value ? parseInt(headInfo4.value, 10) : ''
      headInfo4.description = selectn('stats.all.target.displayName', inning) || ''
      inningStatus = selectn('inningStatus', inning) || ''
      inningStatusDesc = selectn('inningStatusDesc', inning) || ''

      headInfo3Text = (headInfo3.value === 1) ? '(following on)' : ''

      oversLabel = (headInfo4.value < 2) ? 'run' : 'runs'
      if(headInfo4.value) {
        headInfo4Text = `(${headInfo4.description}: ${headInfo4.value} ${oversLabel})`
        headInfo4Text = headInfo4Text.toLowerCase()
      }
    }

    switch (idx) {

      //case 1: 1st innings max overs
      case 1:
        inningsHeaderInfo = headInfo1Text
        break
      //case 2: 2nd innings target and max overs
      case 2:
        inningsHeaderInfo = headInfo2Text
        break
      //case 3: 3rd if followon yes
      case 3:
        inningsHeaderInfo = headInfo3Text
        break
      //case 4: 4th target text with no overs
      case 4:
        inningsHeaderInfo = headInfo4Text
        break
    }
    if(matchFlags.isTestMatch === true) {
      if(inningStatus === 'forfeit') {
        inningsHeaderInfo = `${inningsHeaderInfo} (${inningStatusDesc})`
      }
    }
    return inningsHeaderInfo
  },

  /**
   * Get information on fall of wicket
   * fall of wicket = runs/wicket num (overs ov)   *
   * @param  {Array}  allFows all fall of wickets
   * @return {String}         fowString
   */
  getLastFOWDetails( allFows = [] ) {
    // default values for fall of wicket & last wicket (last out player)
    let fowString = ''
    // Proceed only if fall of wickets exists
    if (allFows.length > 0) {
      let numWickets = allFows.length
      let lastFow = allFows[ numWickets - 1 ] || {}
      // Extract fall of wicket details
      if (lastFow.hasOwnProperty('wicketNumber') && lastFow.hasOwnProperty('runs')) {
        let fow =[ lastFow.runs, lastFow.wicketNumber ].join('/')
        fowString = fow
        // Append over data if available
        if (lastFow.hasOwnProperty('wicketOver')) {
          fowString = `${fow} (${lastFow.wicketOver} ov)`
        }
      }
    }
    //console.info('getLastFOWDetails', fowDetails)

    return fowString
  },

  /**
   * get last Bat Info by comparing currentInningsFows and rosters
   * eg: lastBat = Saqlain Haider c SF Burger b Williams 29 (125m 100b 1x4 0x6) SR: 29.00
   * @param {Object} currentInnings current Innings Info
   * @param {Object} rostersNode complete rosters node
   * @returns {String} lastBatInfo
   */
  getLastBatInfo(currentInnings={}, rostersNode={}) {
    let currPeriod = currentInnings.period || ''
    let currBatTeam = currentInnings.teamId || ''
    let rosters = []
    let rawFows = currentInnings.rawFows || []
    //do the rest if atleast one fow exists
    if(!rawFows.length) {
      return ''
    }
    let lastFow = rawFows[rawFows.length-1] || {}
    let lastFowId = selectn('athlete.id', lastFow) || ''
    let lastFowInfo = {}
    if(currPeriod && currBatTeam) {
      //get current batting team rosters
      for(let i=0; i<rostersNode.length; i++) {
        let teamId = selectn(`[${i}].team.id`,rostersNode) || ''
        if(teamId == currBatTeam) {
          rosters = selectn(`[${i}].roster`, rostersNode)
        }
      }
    }
    //get lastfow player's roster
    if(rosters.length && lastFowId) {
      lastFowInfo = common.getPlayerById(rosters, lastFowId)
    }
    let linescore = selectn(`linescores[${currPeriod-1}]`, lastFowInfo) || {}
    let athleteName = selectn('athlete.displayName', lastFowInfo)
    // build lastBat markup using linescore of lastFow taken from rosters node
    let lastBatMarkup = helpers.getLastBatMarkup(linescore, athleteName) || ''
    return lastBatMarkup
  },
  /**
   * helper to reduce complexity framing lastFow markup
   * @param {Object} linescore linescores taken from roster node
   * @param {String} athlete athlete name for whom lastfowMarkup is being constructed
   * @returns {String} full markup of lastFow
   */
  getLastBatMarkup(linescore={}, athlete='') {
    let outDetail = ''
    let lastBatMarkup = ''
    let stats = {}
    if(athlete.length) {
      outDetail = selectn('statistics.batting.outDetails.shortText', linescore) || ''
      let categories = selectn('statistics.categories[0].stats', linescore) || []
      for(let i=0; i<categories.length; i++) {
        let iCat = categories[i] || {}
        switch(iCat.name) {
          case 'minutes' :
            stats.mins = iCat.displayValue ? `${iCat.displayValue}m` : ''
            break
          case 'runs' :
            stats.runs = iCat.displayValue || ''
            break
          case 'ballsFaced' :
            stats.balls = iCat.displayValue ? `${iCat.displayValue}b` : ''
            break
          case 'fours' :
            stats.fours = iCat.displayValue ? `${iCat.displayValue}X4` : ''
            break
          case 'sixes' :
            stats.sixes = iCat.displayValue ? `${iCat.displayValue}X6` : ''
            break
          case 'strikeRate' :
            stats.sr = iCat.displayValue ? `SR: ${iCat.displayValue}` : ''
            break
        }
      }
      lastBatMarkup = `${athlete} ${outDetail} ${stats.runs || ''} `
      lastBatMarkup += `(${stats.mins || ''} ${stats.balls || ''} ${stats.fours || ''} ${stats.sixes || ''}) ${stats.sr || ''}`
    }
    return lastBatMarkup
  },
  /**
   * Get last partnership information
   * It frames markup like " 20 runs, 7.5 overs, RR: 2.55 (Sharma 18, Pandya 2) "
   * @param  {Array}  allPships all partnerships from innings
   * @return {String}           runs scored and overs taken for last partnership
   */
  getLastPship( allPships = [] ) {
    // default string to return
    let pshipMrkp = ''
    // Proceed only if partnerships array exists
    if (allPships.length > 0) {
      let numPships = allPships.length
      // Get last node for latest partnership
      let lastPship = allPships[ numPships - 1 ] || {}
      let pshipRuns = lastPship.hasOwnProperty('runs') ? `${lastPship.runs} ${lastPship.runs == 1 ? 'run' : 'runs'}` : ''
      let pshipRR = lastPship.hasOwnProperty('runRate') ? 'RR: '+lastPship.runRate : ''
      let pshipOvers = lastPship.hasOwnProperty('overs') ? lastPship.overs+' overs' : ''
      let pshipPlayer1 = selectn('batsmen[0].athlete.displayName', lastPship)
      let pshipPlayer1Runs = selectn('batsmen[0].runs', lastPship) || ''
      let pshipPlayer2 = selectn('batsmen[1].athlete.displayName', lastPship)
      let pshipPlayer2Runs = selectn('batsmen[1].runs', lastPship) || ''
      pshipMrkp += `${pshipRuns}, ${pshipOvers}, ${pshipRR} `
      if(pshipPlayer1 && pshipPlayer2) {
        pshipMrkp += `(${pshipPlayer1} ${pshipPlayer1Runs}, ${pshipPlayer2} ${pshipPlayer2Runs})`
      }
    }
    return pshipMrkp
  },


  /**
   * Get runs scored in last N overs for an innings.
   * recentOvers and runInRecentOvers are available only for live innings
   *
   * @param  {Object} innings innings object from all innings
   * @return {Object}         showModule: show this module or not
   *                          duration : pick up value from feed, usually 5/10
   *                          runs: number of runs scored in this duration
   */
  getLastNForInnings( innings = {} ) {
    // default return object
    let lastNForInnings = {
      showModule: true,
      duration : selectn('stats.all.recentOvers.displayValue', innings) || '',
      runs : selectn('stats.all.runInRecentOvers.displayValue', innings) || '',
      wickets: selectn('stats.all.wicketInRecentOvers.displayValue', innings) || '',
      rr : selectn('stats.all.runRateInRecentOvers.displayValue', innings) || ''
    }
    // if either of duration or runs is empty string, do now show this module
    if (lastNForInnings.duration == '' || lastNForInnings.runs == '' || lastNForInnings.wickets == '') {
      lastNForInnings.showModule = false
    }
    return lastNForInnings
  },

  getCurrentInnings(competition = {}, details = [], rostersNode = [], allInnings, sessionInfo, gameState, params = {}, host, matchFlags = {}, device = {}) {
    // First get active players. Then split them in active batsmen & bowlers
    const currentPeriod = helpers.getCurrentPeriod(competition)
    const recentOvers = helpers.getRecentOvers(details, currentPeriod)
    const activePlayers = common.getActivePlayers(rostersNode)
    const activeBatsmen = helpers.getActivePlayersByRole(activePlayers, 'batting', currentPeriod)
    const activeBowlers = helpers.getActivePlayersByRole(activePlayers, 'bowling', currentPeriod)
    let idx = currentPeriod - 1
    //console.info('activeBatsmen', activeBatsmen)
    //console.info('activeBowlers', activeBowlers)
    const showFullStats = device.desktop === true || device.tablet === true
    let currentInnings = helpers.getCurrentInningsDetails(allInnings)
    let crr = selectn('stats.all.runRate.displayValue', currentInnings) || ''

    // Return crr if current innings and active players data is not found (required as gp2.0 design)
    if (typeof currentInnings.period === 'undefined' || activePlayers.length === 0) {
      return {
        sessionInfo,
        crr
      }
    }

    let playerTrackingName = common.createTrackingName([ params.sport, 'game', params.pageType, 'player' ])
    //console.info('playerTrackingName', playerTrackingName)

    // Prep data structure for current players
    let activePlayerStats = {
      batsmen : {
        player1 : {
          href : '#',
          name : '',
          stats : [],
          trackingName : playerTrackingName
        },
        player2 : {
          href : '#',
          name : '',
          stats : [],
          trackingName : playerTrackingName
        }
      },
      bowlers : {
        player1 : {
          href : '#',
          name : '',
          stats : [],
          trackingName : playerTrackingName
        },
        player2 : {
          href : '#',
          name : '',
          stats : [],
          trackingName : playerTrackingName
        }
      }
    }

    // For Batsman table: limited overs - Last 5 ovs. For tests - Last 10 ovs.
    let lastN = 5
    let lastNOvers = `Last ${lastN} ovs`

    const numActiveBatsmen = activeBatsmen.length
    const numActiveBowlers = activeBowlers.length

    // set default values for featurephone current players
    let liteBatsmen = []
    let liteBowlers = []

    // loop over batmen and extract required stats
    for (var i = 0; i < numActiveBatsmen; i++) {
      let batsman = activeBatsmen[i]
      // Suffix * to batsmen display name since batsman is on strike - e.g. Sachin Tendulkar*
      let isStriker = batsman.activeName === 'striker' ? true : false
      let target = activePlayerStats.batsmen['player' + (i + 1)] || {}
      const athleteName = selectn('athlete.name', batsman)
      const athleteDisplayName = selectn('athlete.displayName', batsman)
      target.name = (isStriker === true) ? (athleteName + '*') : athleteName
      target.displayName = (isStriker === true) ? (athleteDisplayName + '*') : athleteDisplayName
      target.playerId = selectn('athlete.id', batsman)
      target.href = common.extractUrl(batsman.athlete, 'Player Profile', 'desktop')
      // Batting style is in style[0]
      let playerStyle = selectn('athlete.style[0].shortDescription', batsman) || ''
      if (playerStyle !== '' && typeof playerStyle === 'string') {
        playerStyle = playerStyle.toLowerCase()
      }
      target.style = playerStyle

      let _runs = selectn('combinedStats.all.runs.displayValue', batsman) || ''
      let _ballsFaced = selectn('combinedStats.all.ballsFaced.displayValue', batsman) || ''
      let _fours = selectn('combinedStats.all.fours.displayValue', batsman) || ''
      let _sixes = selectn('combinedStats.all.sixes.displayValue', batsman) || ''
      let _strikeRate = selectn('combinedStats.all.strikeRate.displayValue', batsman) || ''

      // Create an array of required stats
      target.stats = [
        _runs,
        _ballsFaced,
        _fours,
        _sixes,
        _strikeRate
      ]

      if (showFullStats) {
        target.stats = target.stats.concat([
          helpers.getPVP(selectn('combinedStats.brief.pvp', batsman)),
          helpers.getPVP(selectn('combinedStats.brief.battingRecent', batsman)),
          selectn('combinedStats.career.matches.displayValue', batsman) || '',
          selectn('combinedStats.career.runs.displayValue', batsman) || '',
          selectn('combinedStats.career.highScore.displayValue', batsman) || '',
          selectn('combinedStats.career.battingAverage.displayValue', batsman) || ''
        ])
      }

      lastN = selectn('combinedStats.brief.battingRecent.overSpan', batsman) || ''
      lastNOvers = `Last ${lastN} ovs`
      // Check if this player has video id associated with it
      let batVideoId = typeof selectn(`linescores[${idx}].media.id`, batsman) !== 'undefined' ? true : false
      //console.info('batVideoId', batVideoId)
      // If video is available, set video details on player object & update flag
      if (batVideoId === true) {
        target.hasVideoId = true
        target.videoId = selectn(`linescores[${idx}].media.id`, batsman)
        target.video = batsman.media || {}
      }
      // Stats for featurephone
      let liteStats = {
        playerid : target.playerId,
        name : target.name,
        style : target.style,
        stats : [
          { text : 'R', value : _runs },
          { text : 'B', value : _ballsFaced },
          { text : '4S', value : _fours },
          { text : '6S', value : _sixes },
          { text : 'SR', value : _strikeRate }
        ]
      }
      liteBatsmen.push(liteStats)
    }

    // loop over bowlers and extract required stats
    for (var j = 0; j < numActiveBowlers; j++) {
      let bowler = activeBowlers[j]
      //console.info('bowler', bowler)
      let target = activePlayerStats.bowlers['player' + (j + 1)] || {}
      target.name = selectn('athlete.name', bowler)
      target.displayName = selectn('athlete.displayName', bowler)
      target.playerId = selectn('athlete.id', bowler)
      target.href = common.extractUrl(bowler.athlete, 'Player Profile', 'desktop')

      // Bowling style
      let playerStyleArray = selectn('athlete.style', bowler) || []
      let playerStyle = ''
      //usually one bowler has one batting style and one bowling style
      //Added for the case: if a bowler has more than one bowling style like for player Tendai Chisoro(id: "235519")
      if (playerStyleArray.length > 2) {
        let bowlerStyleMap = []
        playerStyleArray.map((s = {}) => {
          const { type: bowlerStyleType = '', shortDescription: bowlerStyleTypeDesc = '' } = s
          if (bowlerStyleTypeDesc && bowlerStyleType && bowlerStyleType === 'bowling') {
            bowlerStyleMap.push(bowlerStyleTypeDesc)
          }
        })
        playerStyle = bowlerStyleMap.join('/')
      } else {
        playerStyle = selectn('athlete.style[1].shortDescription', bowler) || ''
      }
      if (playerStyle !== '' && typeof playerStyle === 'string') {
        playerStyle = playerStyle.toLowerCase()
      }
      target.style = playerStyle

      let _overs = selectn('combinedStats.all.overs.displayValue', bowler) || ''
      let _maidens = selectn('combinedStats.all.maidens.displayValue', bowler) || ''
      let _conceded = selectn('combinedStats.all.conceded.displayValue', bowler) || ''
      let _wickets = selectn('combinedStats.all.wickets.displayValue', bowler) || ''
      let _economyRate = selectn('combinedStats.all.economyRate.displayValue', bowler) || ''

      // Create an array of required stats
      target.stats = [
        _overs,
        _maidens,
        _conceded,
        _wickets,
        _economyRate
      ]

      if (showFullStats) {
        target.stats = target.stats.concat([
          selectn('combinedStats.all.dots.displayValue', bowler) || '-',
          selectn('combinedStats.all.foursConceded.displayValue', bowler) || '-',
          selectn('combinedStats.all.sixesConceded.displayValue', bowler) || '-',
          helpers.getSpellDetails(selectn('combinedStats.brief.currentSpell',bowler)),
          selectn('combinedStats.career.matches.displayValue', bowler) || '',
          selectn('combinedStats.career.wickets.displayValue', bowler) || '',
          selectn('combinedStats.career.bestBowlingFigures.displayValue', bowler) || '',
          selectn('combinedStats.career.bowlingAverage.displayValue', bowler) || ''
        ])
      }

      // Check if this player has video id associated with it
      let bowlVideoId = typeof selectn(`linescores[${idx}].media.id`, bowler) !== 'undefined' ? true : false
      //console.info('bowlVideoId', bowlVideoId)
      // If video is available, set video details on player object & update flag
      if (bowlVideoId === true) {
        target.hasVideoId = true
        target.videoId = selectn(`linescores[${idx}].media.id`, bowler)
        target.video = bowler.media || {}
      }

      // Stats for featurephone
      let liteStats = {
        playerid : target.playerId,
        name : target.name,
        style : target.style,
        stats : [
          { text : 'O', value : _overs },
          { text : 'M', value : _maidens },
          { text : 'R', value : _conceded },
          { text : 'W', value : _wickets },
          { text : 'ECON', value : _economyRate }
        ]
      }
      liteBowlers.push(liteStats)
    }

    //console.info('liteBatsmen', liteBatsmen)
    //console.info('liteBowlers', liteBowlers)
    //const lastOver = details[0] || { batsman:{}, otherBatsman:{}, bowler:{}, otherBowler:{} }

    // Get last wicket, fall of wicket and current partnership details
    let lastFowInfo = helpers.getLastFOWDetails(currentInnings.rawFows)
    let partnership = helpers.getLastPship(currentInnings.rawPartnerships)
    let lastNForInnings = helpers.getLastNForInnings(currentInnings)
    let reviews = helpers.getReviews(competition)
    let generalClassCard
    let lastBatInfo = helpers.getLastBatInfo(currentInnings, rostersNode)
    const innings = {
      heading: currentInnings.title || '',
      sessionInfo: sessionInfo,
      crr: crr,
      rrr: selectn('stats.all.requiredRunRate.displayValue', currentInnings) || '',
      minRemainingOvers: selectn('stats.all.remainingOvers.displayValue', currentInnings) || '',
      lastNForInnings: lastNForInnings,
      lastBat: lastBatInfo,
      reviews: reviews,
      fow: lastFowInfo,
      partnership: partnership,
      activePlayers: activePlayers,
      players: {
        batsmen: {
          keys: [
            { id: 'R' },{ id: 'B' },{ id: '4s', className: 'u-tt-l' },{ id: '6s', className: 'u-tt-l' },{ id : 'SR' }
          ],
          list: [ activePlayerStats.batsmen.player1, activePlayerStats.batsmen.player2 ]
        },
        bowlers: {
          keys: [
            { id: 'O' },{ id: 'M' },{ id: 'R' }, { id: 'W' },{ id: 'Econ' }
          ],
          list: [ activePlayerStats.bowlers.player1, activePlayerStats.bowlers.player2 ]
        }
      },
      liteBatsmen,
      liteBowlers
    }

    if (showFullStats) {
      generalClassCard = selectn('class.generalClassCard', competition) || ''

      if (typeof generalClassCard === 'string') {
        generalClassCard = generalClassCard.toUpperCase() + ' CAREER'
      }

      // Append batsmen live-career data
      innings.players.batsmen.keys = innings.players.batsmen.keys.concat([
        { id : 'This Bowler', colspan: '3' }, { id :  lastNOvers, className: 'col-highlight' },
        { id: 'mat' }, { id: 'runs' }, { id: 'hs' }, { id: 'ave' }
      ])

      // Append bowler live-career data
      innings.players.bowlers.keys = innings.players.bowlers.keys.concat([
        { id: '0s', className: 'u-tt-l' }, { id: '4s', className: 'u-tt-l' }, { id: '6s', className: 'u-tt-l' },
        { id: 'This Spell', className: 'col-highlight' },
        { id: 'mat' },{ id: 'wkts' },{ id: 'bbi' },{ id: 'ave' }
      ])
    }


    return { recentOvers, ...innings, generalClassCard, showFullStats }
  },


  /**
    * Join players name & style - e.g. Sachin Tendulkar (rhb)
    * @param  {string} playerName  player name
    * @param  {string} playerStyle player style
    * @return {string}             playerJoinString
    */
  getPlayerDisplayName(playerName = '', playerStyle = '') {
    // Default return value should be name of player
    let playerDisplayName = playerName
    // Array to hold name & style
    let display = []
    // Push name only if it is not empty
    if (playerName !== '') {
      display.push(playerName)
    }
    // Push formatted style only if it is not empty
    if (playerStyle !== '' && typeof playerStyle === 'string') {
      // Convert to lowercase
      let lcStyle = playerStyle.toLowerCase()
      display.push(`(${lcStyle})`)
    }
    // Update displayable only if display array is not empty
    if (display.length !== 0) {
      playerDisplayName = display.join(' ')
    }
    return playerDisplayName
  },
  /**
   * Forms an array of current batsmen and bowlers
   * @param  {Object} rosters rosters node
   * @param  {Object} period   innings period
   * @return {Array}        current batsmen and bowlers
   */
  getCurrentPlayersIds(rosters = {},period='') {
    const batsmen = []
    const bowlers = []

    rosters.map((r) => {
      const activeName = r.activeName || ''
      let playerId = selectn('athlete.id', r)
      switch(activeName) {
        case 'striker':
          batsmen.unshift({ playerId :playerId, period : period })
          break

        case 'non-striker':
          batsmen.push({ playerId :playerId, period : period })
          break

        case 'current bowler':
          bowlers.unshift({ playerId :playerId, period : period })
          break

        case 'previous bowler':
          bowlers.push({ playerId :playerId, period : period })
          break
      }
    })
    return { bowlers, batsmen }
  },


  /**
   * Get best players from a match (across all innings).
   * Pick up 2 best bowlers and 2 best batmen.
   *
   * @param  {Array}  leadersNode leaders node from feed
   * @return {Object}             object with list of 2 batsmen and bowlers
   */
  getBestPlayersFromMatch( leadersNode = [] ) {
    //console.info('getBestPlayersFromMatch, leadersNode', leadersNode)

    // Default return type
    let batsmen = []
    let bowlers = []
    // Store list of all top players in these arrays
    let allBatters = []
    let allBowlers = []

    const numLeaders = leadersNode.length

    // Iterate over leaders node and extract top players
    for (var i = 0; i < numLeaders; i++) {
      let team = leadersNode[i]
      let ls = team.linescores || []
      let numLS = ls.length
      // Iterate over each linescore. it could be either batting/bowling
      for (var j = 0; j < numLS; j++) {
        let batOrBowl = ls[j] || {}
        // In which period this performance happened?
        let period = batOrBowl.period || ''
        // Get all(3) top players for each innings
        let players = selectn('leaders[0].leaders', batOrBowl) || []
        // If top players are found and isBatting property exists
        if (players.length !== 0 && batOrBowl.hasOwnProperty('isBatting')) {
          // Store for easier lookup
          let pushTo = []
          // Append a player to batters array if isBatting is true
          if (batOrBowl.isBatting === true) {
            pushTo = allBatters
          }
          // Append a player to bowlers array if isBatting is false
          if (batOrBowl.isBatting === false) {
            pushTo = allBowlers
          }
          // Iterate over best players and append them to allBatters/allBowlers
          for (var k = 0; k < players.length; k++) {
            let player = players[k] || {}
            let playerId = selectn('athlete.id', player)
            /**
             * Append to array only if player id is found.
             * Also set a period property which will be used by other functions
             */
            if (typeof playerId !== 'undefined') {
              //console.info('player', player)
              player.period = period
              pushTo.push(player)
            }
          }
        }
      }
    }

    /**
     * If top batsmen and bowler arrays are not empty, sort them on order key
     * Then get the first 2 elements (since top 2 batman and bowlers need to be
     * shown)
     */
    if (allBatters.length !== 0) {
      allBatters.sort(common.compareEntities('order'))
      batsmen = allBatters.slice(0,2)
    }

    if (allBowlers.length !== 0) {
      allBowlers.sort(common.compareEntities('order'))
      bowlers = allBowlers.slice(0,2)
    }

    return { batsmen, bowlers }
  },


  /**
   * Get athlete ids and periods of best batsmen and bowlers
   * @param  {Object} bestPlayers object with batsmen and bowlers arrays
   * @return {Object}             players object with batsmen & bowlers array
   */
  getBestPlayersIds( bestPlayers = {}) {
    // default return type
    let players = {
      batsmen : [],
      bowlers : []
    }
    // Iterate over best players and get the player ids.
    for ( let bb in bestPlayers) {
      // bb should be either batsmen or bowlers
      if (bestPlayers.hasOwnProperty(bb) === false ||
          players.hasOwnProperty(bb) === false) {
        continue
      }
      // batsmen/bowlers array
      let batOrBowl = bestPlayers[bb]
      let numPlayers = batOrBowl.length
      // loop over players and extract player id and respective period of play
      for (var i = 0; i < numPlayers; i++) {
        let player = batOrBowl[i]
        let playerId = selectn('athlete.id', player)
        let period = selectn('period',player)
        if (playerId !== undefined) {
          players[bb].push({ playerId : playerId, period : period })
        }
      }
    }
    //console.info('players', players)
    return players
  },

  /**
   * Get team details of an athlete.
   * Create a map to lookup athlete ids to their team objects.
   * @param  {Array}  rosters rosters node
   * @return {Object}         keys are athlete ids, value is team object
   */
  getTeamOfAthletes( rosters = [] ) {
    // default return value
    let athleteToTeam = {}

    const numRosters = rosters.length
    // Iterate over rosters of both the teams
    for (var j = 0; j < numRosters; j++) {
      let team = rosters[j].team || {}
      let teamRoster = rosters[j].roster || []
      // iterate over each player and populate object
      for (var k = 0; k < teamRoster.length; k++) {
        let athlete = teamRoster[k]
        let athleteId = selectn('athlete.id', athlete)
        if (typeof athleteId !== 'undefined') {
          athleteToTeam[athleteId] = team
        }
      }
    }
    return athleteToTeam
  },

  /**
   * Get all statistics of an innings from linescores node
   * @param  {Object} inputStats linescores node
   * @return {Object}            stats.brief = top level stats
   *                             stats.all = statistics array converted to map
   */
  getInningsStats(inputStats = {}) {
    // default stats to return
    let stats = {
      all : {},
      brief: {}
    }
    //console.info('inputStats', inputStats)

    // Get all detailed stats
    let allStats = selectn('statistics.categories[0].stats', inputStats) || {}
    stats.all = common.createStatsMap(allStats)

    // Iterate over top level stats and populate brief node
    for (var k in inputStats) {
      // cache value
      let v = inputStats[k]
      // Skip any properties containing an object
      if (v instanceof Object === false) {
        stats.brief[k] = v
      }
    }
    //console.info('stats', stats)
    return stats
  },


  /**
   * Get fall of wickets
   * @param  {Object} inn Innings object
   * @return {Array}     fall of wickets in array format.
   *                     Each item in array is string of format
   *                     wicket num - runs on which wicket fell (player
   *                     who got out, overs at which player got out)
   */
  getInningsFOW(inn = {}) {
    //console.info('inn', inn)
    let fows = []
    // Return empty array if fall of wickets is not found
    if (inn.hasOwnProperty('fow') === false) {
      return fows
    }
    // declare default strings
    let wicketRuns = ''
    let playerOvers = ''
    // Extract fall of wickets node
    let fowickets = inn.fow || []
    let numFOW = fowickets.length
    // iterate over each of fall of wicket and prep the required string.
    for (var i = 0; i < numFOW; i++) {
      let fow = fowickets[i]
      let fowType = fow.fowType || ''
      if (fow.hasOwnProperty('wicketNumber') === false) {
        continue
      }
      // Get #wicket fallen and runs on scoreboard
      let wicketNum = fow.wicketNumber
      let runs = fow.runs
      // If runs is undefined, make runs as empty string
      if (typeof runs === 'undefined') {
        runs = ''
      }
      if (parseInt(runs) > 0 && fowType !== 'out') {
        runs = runs + '*'
      }
      // Join on - only if wicketNum is not falsy and runs is not empty
      if (wicketNum) {
        wicketRuns = [ wicketNum, runs ].join('-')
        //console.info('wicketRuns', wicketRuns)
      }
      // Get name of batsman who got out and overs
      let player = selectn('athlete.displayName', fow) || ''
      let overs = fow.wicketOver || ''

      // Join on , only if player name is not empty
      if (player !== '') {
        playerOvers = `${player}${fowType == 'out' ? (overs ? (', ' + overs + ' ov') : '') : (fowType.length ? ', '+fowType : '')}`
      }
      // If player & over details are missing, push only wicket/run otherwise push both to fall of wickets array
      if (wicketRuns !== ''){
        if(playerOvers !== ''){
          fows.push(`${wicketRuns} (${playerOvers})`)
        } else {
          fows.push(`${wicketRuns}`)
        }
      }
    }
    //console.info('fows', fows)
    return fows
  },

  /**
   * Get array of all innings
   * @param  {Object}   competition   competitors and other details
   * @param  {Object}   params        router params
   * @param  {Object}   query         router query
   * @param  {Boolean}  detailedStats true if all stats for this innings are required
   * @param  {Object}   matchFlags    flags for this match
   * @param  {Object}   hrefConfig    config object for create href function
   * @return {Array}                   Details of each innings
   */
  getAllInnings(competition = {}, params = {}, query = {}, detailedStats = false, matchFlags = {}, hrefConfig) {
    let allInnings = []
    // Return empty array If competitors array is missing
    if (competition.hasOwnProperty('competitors') === false) {
      return allInnings
    }
    let innText = 'Innings'
    // default delimiter for innings related strings
    let delimiter = ' '
    let competitors = competition.competitors
    competitors.map(function (competitor) {
      let teamAbbreviation = selectn('team.abbreviation', competitor)
      let teamDisplayName = selectn('team.displayName', competitor)
      let teamId = selectn('team.id', competitor)

      /**
       * Construct the string for a team in innings dropdown
       * For limited over matches, it should be teamName Innings
       * For non limited over matches, it should be teamName 1st/2nd Innings
       */
      let innNum = ''
      //console.info('competitor', competitor)
      // Create a clone before modifying the original Array
      let linescores = competitor && Array.isArray(competitor.linescores) ? competitor.linescores.slice(0) : []
      // reordering linescores
      linescores = linescores.sort(function (a, b) {
        return a.period - b.period
      })
      if (matchFlags.limitedOvers === false) {
        innNum = '1st'
      }
      linescores.map(function (score) {
        // Used to build team name & team innings string
        let teamInn = [ teamAbbreviation ]
        // Use this to construct full string - team name nth Innings
        let inningsTitle = [ teamDisplayName ]
        // Use this to construct innings display - nth Innings
        let inningsDesc = [ innText ]
        let period = parseInt(score.period, 10)
        // Build all innings array only if period is positive integer and team is batting
        if (score.isBatting === true && period >= 0) {
          if (period >= 3) {
            innNum = '2nd'
          }
          // Do not append if inn num is empty string (for limited over matches)
          if (innNum !== '') {
            teamInn.push(innNum)
            inningsTitle.push(innNum)
            inningsDesc.unshift(innNum)
          }
          teamInn.push(innText)
          inningsTitle.push(innText)
          // Join the teamInn array on space char
          let linkName = teamInn.join(delimiter)
          // Full innings title
          let inningsTitleString = inningsTitle.join(delimiter)
          // construct url for innings commentary
          let baseUrl = '#'
          let linkUrl = '#'
          let matchId = 'gameId' in params ? parseInt(params.gameId, 10) : ''
          let leagueId = 'league' in params ? parseInt(params.league, 10) : ''
          if (matchId !== '' && leagueId !== '') {

            linkUrl = common.createHref({ ...params, ...hrefConfig, pageType: 'commentary', query: { innings: period, filter : query.filter, 'edition-host': query['edition-host'] } })
            baseUrl = common.createHref({ ...params, ...hrefConfig, pageType: 'commentary', query: { innings: period, 'edition-host': query['edition-host'] } })
          }
          // Create info object for this innings
          let innDetails = {
            period : period,
            teamName: teamAbbreviation,
            teamDisplayName: teamDisplayName,
            innDisplayName: inningsDesc.join(delimiter),
            teamId:teamId,
            text: linkName,
            name: linkName,
            href: linkUrl,
            baseUrl,
            title: inningsTitleString,
            isCurrent: score.isCurrent == 1 ? true : false,
            stats : {}
          }
          // Get all stats for this innings if detailedStats flag is true
          if (detailedStats === true) {
            innDetails.stats = helpers.getInningsStats(score)
            innDetails.fows = helpers.getInningsFOW(score)
            innDetails.rawFows = score.fow || [],
            innDetails.rawPartnerships = score.partnerships || []
          }
          if (score.score === 'forfeit') {
            innDetails.inningStatus = score.score
            innDetails.inningStatusDesc = score.description
          } else if(score.description === 'declared') {
            innDetails.inningStatus = 'declared'
            innDetails.inningStatusDesc = score.description
          }
          allInnings.push(innDetails)
        }
      })
    })

    // Sort the innings on the period key
    allInnings.sort(function (inn1, inn2) {
      if (inn1.hasOwnProperty('period') && inn2.hasOwnProperty('period')) {
        if (inn1.period > inn2.period) {
          return 1
        }
        if (inn1.period < inn2.period) {
          return -1
        }
      }
      // By default, keep them as is
      return 0
    })
    return allInnings
  },


  /**
   * Get innings which will be selected in the innings dropdown filter
   * @param  {Array}  allInnings  details of all innings
   * @param  {Array}  cmtryInns   details of commentary innings
   * @param  {Object} query       request query
   * @param  {Object} showSuperOver flag to check if super over
   * @return {String}             innings which will be selected
   */
  getSelectedInnings(allInnings = [], cmtryInns = [], query = {}, showSuperOver) {
    // Default to latest element from the allInnings array
    let selectedInnings = allInnings[allInnings.length - 1] || {}

    if (showSuperOver) {
      selectedInnings = cmtryInns[cmtryInns.length - 1]
      return selectedInnings
    }

    // Check if User has requested any particular innings
    if (query.hasOwnProperty('innings') && (/^superover$/ig).test(query.innings) === false) {
      let period = query.innings
      //console.info('period', period)
      // Map required for allInnings array lookup
      let periodToInnings = {
        '1' : 0,
        '2' : 1,
        '3' : 2,
        '4' : 3
      }
      // Number of available innings
      let totalInnings = allInnings.length
      // Proceed only if requested period is valid
      if (periodToInnings.hasOwnProperty(period)) {
        let innings = periodToInnings[period]
        // Make sure the requested innings falls in the total innings of match
        if (innings <= totalInnings) {
          selectedInnings = allInnings[innings] || {}
        }
      }
    }
    //console.info('selectedInnings', selectedInnings)
    return selectedInnings
  },
  getLeadersId(leadersNode = []) {
    const leaders = selectn('[0].leaders',leadersNode) || []
    let playersId = []

    // Pick the first 2 players
    leaders.slice(0,2).map((leader) => {
      const id = selectn('athlete.id' ,leader)
      if(id) playersId.push(id)
    })

    return playersId
  },

  getLeadersData(players = {}, statMap, period) {
    let leadersData = []

    Object.keys(players).map(b => {

      const currentPlayer = players[b]
      const linescores = currentPlayer.linescores || []

      linescores.map(l => {
        if (l.period !== period) {
          return
        }
        const href = common.extractUrl(currentPlayer.athlete, 'Player Profile', 'desktop')
        const statsNode = selectn('statistics.categories[0].stats', l) || []
        const stats = !statMap ? statsNode : common.createStatsMap(
          common.filterStatsByNames(statsNode, statMap)
        )

        leadersData.push({
          name: selectn('athlete.displayName', currentPlayer),
          href,
          stats
        })
      })
    })

    return leadersData
  },

  /**
   * Build the innings dcropdown for commentary
   *
   * @param {Array} allInnings  all the innings
   * @param {Array} tieBreaker  tiebreaker arrary
   * @param {Object} params     params
   * @param {Object} hrefConfig href config
   * @returns {Array} commenatryInnings
   */
  getCmtryInns(allInnings = [], tieBreaker = [], params, hrefConfig) {
    let innings = [ ...allInnings ]

    if (tieBreaker.length > 0) {
      innings.push({
        text: 'Super Over',
        href: common.createHref({ ...params, ...hrefConfig, pageType: 'commentary', query: { innings: 'superover' } })
      })
    }

    return innings
  },

  /**
   * build filter dropdown data for commentary
   * @param  {Object} selInnings selected innings
   * @return {Array}             array of dropdown item
   */
  getCmtryFilter(selInnings) {
    // innings hyperlink of selected inngings
    const innBaseUrl = selectn('baseUrl', selInnings) || ''
    const stats = selectn('stats.brief', selInnings) || {}
    const { wickets = 0, fours = 0, sixes = 0 } = stats
    let filterItems = []

    // add full commentary as first filter
    filterItems.push({
      text : 'Full commentary',
      playtype: 'full',
      href : innBaseUrl + '&filter=full'
    })

    // add 'wicket' filter item if any wickets
    if (wickets > 0) {
      filterItems.push({
        text: 'Wickets',
        playtype: 'wickets',
        href: innBaseUrl + '&filter=wickets'
      })
    }

    // add 'boundaries' filter item if 4's / 6's
    if ((fours+sixes) > 0) {
      filterItems.push({
        text: 'Boundary',
        playtype: 'boundary',
        href: innBaseUrl + '&filter=boundary'
      })
    }

    filterItems.push({
      text : 'Highlights',
      playtype: 'highlight',
      href: innBaseUrl + '&filter=highlight'
    })
    return filterItems
  },

  /**
   * select current filter for based on query-string or default
   * @param  {Array}  cmtryFilter array of filters
   * @param  {Object} query       router query
   * @return {Object}             selected filter
   */
  getSelCmtryFilter(cmtryFilter = [], query = {}) {
    // select full commentary by default
    const filter = query.filter || 'full'
    let selFilter

    for(let item of cmtryFilter) {
      // select the current filter based router query
      if (item.playtype === filter) {
        selFilter = item
        break
      }
    }

    return selFilter
  },
  getMetaData(params, competition = {} , gameStrip, matchDetails, standings, gameState, league = {}, matchDescription = '', matchTitle, matchLink='', edition, season = {}, hrefConfig = {}, article, isTournament, leagueName) {
    const meta = {}

    let date = competition.date
    let dateValid = date ? new Date(date) : null
    date = moment(dateValid).format('D MMM, YYYY')
    let matchYear = moment(dateValid).format('YYYY')
    const teams = gameStrip.teams || { }
    const homeTeam = teams.home || {}
    const homeTeamName = homeTeam.name || ''
    const awayTeam = teams.away || {}
    const awayTeamName = awayTeam.name || ''
    let seasonYear = selectn('year', season) || ''
    let { sport = '', pageType = '' } = params
    let articleImage = selectn('images[0].url', article) || ''
    // team abbreviations
    let homeTeamAbbr = selectn('home.abbrev', teams) || ''
    let awayTeamAbbr = selectn('away.abbrev', teams) || ''
    let matchNum = competition.description || ''
    let matchWinner = homeTeam.winner === 'true'? homeTeamName : awayTeamName
    let matchLoser = homeTeam.winner === 'false'? homeTeamName : awayTeamName
    let matchSummary = competition.status.summary || ''
    let matchName = competition.name
    let fullScoreWithTitle = competition.title

    // If abbreviation is not found, set abbreviation to team name
    if (homeTeamAbbr === '') {
      homeTeamAbbr = homeTeamName
    }
    if (awayTeamAbbr === '') {
      awayTeamAbbr = awayTeamName
    }

    let matchDataForMeta = {
      fullScore: fullScoreWithTitle,
      homeTeamName: homeTeamName,
      homeTeamAbbr: homeTeamAbbr,
      awayTeamName: awayTeamName,
      awayTeamAbbr: awayTeamAbbr,
      matchNum: matchNum,
      matchSummary: matchSummary,
      gameState: gameState,
      seasonYear: seasonYear,
      leagueName: leagueName,
      isTournament: isTournament,
      matchWinner: matchWinner,
      matchLoser: matchLoser,
      matchName: matchName,
      matchYear: matchYear
    }

    let gamepackageMetaObject = metahelpers.getGamePackageMetaObject(matchDataForMeta, pageType, edition)

    let newSlug = common.getSlugFromUrl(params.gameId, matchLink)
    // Additional info required by Native APP
    meta.gameId = competition.id || ''
    meta.gameState = selectn('status.type.state', competition) || ''
    meta.leagueId = league.id || ''
    meta.leagueUID = league.uid || ''
    meta.leagueName = league.name || ''
    meta.matchDescription = matchDescription
    meta.sport = sport
    meta.title =  gamepackageMetaObject.titleText
    meta.description = gamepackageMetaObject.metaDescriptionText
    meta.keywords = gamepackageMetaObject.metaKeywordsText
    meta.canonical = common.createCanonical(edition, params, hrefConfig, newSlug)
    meta.shareurl = meta.canonical
    meta.website = 'article'
    meta.shortTitle = `${homeTeamAbbr} vs ${awayTeamAbbr}`
    meta.competitionUID = competition.uid || ''
    meta.image = articleImage || 'https://a.espncdn.com/combiner/i?img=/i/espn/espn_logos/espncricinfo.png'

    return meta
  },
  isHidden({ standingsNode = {}, article = {}, partCommentary = {}, statistics = {}, videos = [], matchCoverage = [], hawkEye = {} }) {

    let previewAvailable = false
    let reportAvailable = false

    if(article.type === 'Preview') {
      if(article.id) {
        previewAvailable = true
      }
    }

    if(article.type === 'Recap' || article.type === 'Report') {
      if(article.id) {
        reportAvailable = true
      }
    }
    return {
      preview : !previewAvailable,
      report : !reportAvailable,
      commentary: !partCommentary.enabled,
      statistics: !statistics.enabled,
      video: !videos.length,
      coverage: !matchCoverage.length,
      table: !Object.keys(standingsNode).length,
      hawkeye : !hawkEye.show
    }
  },
  /**
   * Logic to determine if polling should be enabled
   * for the game
   * @param  {String} date      competition.date
   * @param  {String} gameState game state
   * @return {Object}           Polling details
   */
  shouldPoll(date, gameState) {
    const duration = 20000
    const durationInSec = duration / 1000
    let enabled = false

    if(gameState === 'live') {
      enabled = true
    }
    const startTime = moment.utc(new Date())
    const endTime = moment(date)
    const diff = endTime.diff(startTime, 'hours')
    // console.log(startTime.format(), endTime.format(), diff)
    // TODO: update logic
    // polling 12 hours, before and after the game
    if(Math.abs(diff) <= 12) {
      enabled = true
    }
    return { enabled, duration, durationInSec }
  },

  isRosterAvailable(rostersNode =  []) {
    const teamA = selectn('[0].roster', rostersNode) || []
    const teamB = selectn('[1].roster', rostersNode) || []
    return teamA.length && teamB.length
  },

  /**
   * Get DRS - decision reviews system or umpire reviews data
   * @param  {Object} competition competition details
   * @return {Array}             Object with team name and reviews
   */
  getReviews(competition = {}) {
    // default return data
    let drsData = []
    let competitors = competition.competitors || []
    // iterate over competitors
    for (let i = 0; i < competitors.length; i++) {
      let teamInfo = competitors[i] || {}
      // Skip to next team if linescores are not found
      if (teamInfo.hasOwnProperty('linescores') === false) {
        continue
      }
      // Get linescores
      let linescores = teamInfo.linescores || []
      let numLS = linescores.length
      for (let j = 0; j < numLS; j++) {
        let inn = linescores[j] || {}
        // Append reviews only for current innings
        if (inn.hasOwnProperty('isCurrent') && inn.isCurrent == 1) {
          if (inn.hasOwnProperty('reviews')) {
            /** reviews have this structure
              {
                'remaining': '2',
                'reviewsPermitted': '2',
                'successful': '1',
                'unsuccessful': '0'
              }
             *
             **/
            // Prepare input to default data
            let input = {
              name : selectn('team.displayName', teamInfo) || '',
              abbreviation : selectn('team.abbreviation', teamInfo) || '',
              reviews: inn.reviews || {}
            }
            drsData.push(input)
          }
        }
      }

    }
    //console.info('drsData', drsData)
    return drsData
  },

  getAnalytics(params = {}, teams = {}, meta = {}, gameState, edition) {
    const { home = {}, away = {} } = teams
    const isSummary = params.pageType === 'game'
    const isLive = gameState === 'live'
    let type = isSummary ? ( isLive ? 'live' : 'summary' ) : params.pageType
    type = (type === 'video') ? 'videos' : type
    let contentType = `game${type ? ':' + type : ''}`

    let metaSlug = meta.leagueName || ''
    metaSlug = metaSlug.split(' ').join('-').toLowerCase() || ''
    let gameStateProp = 'pre'

    switch(gameState) {
      case 'live':
        gameStateProp = 'in'
        break
      case 'final':
        gameStateProp = 'post'
        break
    }

    let omniture = {
      gameState: gameStateProp,
      pageName : `${params.sport}:${contentType}`,
      gameInfo : `${params.gameId}+${home.name} vs ${away.name}`,
      eVar44: 'gamepackage',
      contentType
    }

    if(edition.isEspnCricinfo) {

      omniture = {
        gameState: gameStateProp,
        pageName : `${metaSlug}:${contentType}`,
        prop5 : `${metaSlug}:game`,
        prop26 : `${metaSlug}`,
        eVar21 : `${metaSlug}`,
        eVar11 : `cricinfo:${metaSlug}:game`,
        channel : `${metaSlug}`,
        gameInfo : `${params.gameId}+${home.name} vs ${away.name}`,
        contentType : 'match'
      }
    }

    const data = {
      omniture,
      chartbeat: {
        title: meta.title
      }
    }

    return data
  },

  /**
   * extract markup for dismissalnode from current wicket
   * @param  {Object}  wicket wicket node from overs node of competition linescores
   * @return {String}  Formatted markup for current wicket
   */
  getWicketStats(wicket={}) {
    //console.info('wicket', wicket)
    let allDetails = []
    let runDetails = []

    let shortText = wicket.shortText || ''
    if (shortText !== '') {
      allDetails.push(shortText)
    }
    let runs = wicket.runs || ''
    if (runs !== '') {
      allDetails.push(runs)
    }

    let minutes = typeof wicket.minutes != 'undefined' ? wicket.minutes : ''
    if (minutes !== '') {
      runDetails.push(`${minutes}m`)
    }
    let ballsFaced = typeof wicket.ballsFaced != 'undefined' ? wicket.ballsFaced : ''
    if (ballsFaced !== '') {
      runDetails.push(`${ballsFaced}b`)
    }
    let fours = typeof wicket.fours != 'undefined' ? wicket.fours : ''
    if (fours !== '') {
      runDetails.push(`${fours}x4`)
    }
    let sixes = typeof wicket.sixes != 'undefined' ? wicket.sixes : ''
    if (sixes !== '') {
      runDetails.push(`${sixes}x6`)
    }

    let runsString = ''
    let finalString = ''

    if (runDetails.length !== 0) {
      runsString = `(${runDetails.join(' ')})`
      allDetails.push(runsString)
    }

    if (allDetails.length !== 0) {
      finalString = allDetails.join(' ')
    }
    //console.info('allDetails', allDetails)
    //console.info('finalString', finalString)
    return `<strong> ${finalString} </strong> `
  },

  /**
   * Is this team batting in current innings
   * @param  {Object}  competitor competitor node from competitions
   * @param  {String} gameState pregame/live/final
   * @return {Boolean}            true if isBatting and isCurrent are true
   */
  isTeamBattingNow: function ( competitor = {}, gameState = '' ) {
    // default return value
    let isTeamBatting = false
    // Cache linescores node
    let linescores = competitor.linescores || []
    // is game live
    const isLive = gameState === 'live' ? true : false
    let numLS = linescores.length
    // Iterate over linescores and check isBatting and isCurrent flags
    for (var i = 0; i < numLS; i++) {
      let inn = linescores[i]
      //console.info('inn', inn, inn.isBatting, inn.isCurrent)
      if (isLive && inn.hasOwnProperty('isBatting') && inn.isBatting == true) {
        if (inn.hasOwnProperty('isCurrent') && inn.isCurrent == 1) {
          isTeamBatting = true
          // current batting team found so break
          break
        }
      }
    }
    return isTeamBatting
  },

  /**
   * Get spell details of bowler.
   * @param  {Object} spell Object with keys - overs, maidens, runs and wickets
   * @return {String}       String in format overs-maidens-runs-wickets
   */
  getSpellDetails: function ( spell = {} ) {
    // default spell details
    let spellDetails = ''
    let delimiter = '-'
    // Return default spell If spell is undefined
    if (typeof spell === 'undefined') {
      return spellDetails
    }
    let spellStats = [ spell.overs, spell.maidens, spell.conceded, spell.wickets ]
    spellDetails = spellStats.join(delimiter)
    if(/^(.)\1+$/.test(spellDetails)) {
      spellDetails = '-'
    }
    return spellDetails
  },

  /**
   * Get current innings object.
   * Iterate over all innings array and pickup latest innings where
   * isCurrent flag is true
   *
   * @param  {Array}  allInnings all innings array
   * @return {Object}            current innings object
   */
  getCurrentInningsDetails: function ( allInnings = [] ) {
    let currentInnings = {}
    for (var i = 0; i < allInnings.length; i++) {
      let innings = allInnings[i]
      if (innings.hasOwnProperty('isCurrent') && innings.isCurrent === true) {
        currentInnings = Object.assign({}, innings)
        break
      }
    }
    return currentInnings
  },

  /**
   * get scoring source details
   * @param  {Object} competition competition
   * @return {Object}             isScoringScource isFeedback, isOptafeed and isDougie
   */
  getScoringSource( competition = {} ) {
    const scoringSource = selectn('competitors[0].linescores[0].source.description', competition) || ''
    let isFeedback = false, isOptafeed = false, isDougie =  false, isEmma = false
    switch(scoringSource.toLowerCase()) {
      case 'feedback':
        isFeedback = true
        break
      case 'optafeed':
        isOptafeed = true
        break
      case 'dougie':
        isDougie = true
        break
      case 'emma':
        isEmma = true
        break
    }
    return {
      isFeedback,
      isOptafeed,
      isDougie,
      isEmma
    }
  },

  /**
   * checks whether a stat is default(-) for all players
   * @param {Object} statsMap statsMap
   * @param {Array} players players
   * @return {Object} statKeys with truthValues
   */
  checkIfStatsAreValid(statsMap={}, players) {
    let statKeys = {}
    for(let key in statsMap) {
      statKeys[key] = false
    }
    for( let i=0; i < players.length; i++ ) {
      let player = players[i] || {}
      let stats = player.stats || []
      if(helpers.checkTruthsOfStatsMap(statKeys))  {
        break
      } else {
        for(let i=0; i< stats.length; i++) {
          if(stats[i].value != '-' && typeof statKeys[stats[i].name] != 'undefined') {
            statKeys[stats[i].name] = true
          }
        }
      }
    }
    return statKeys
  },
/**
 * checks if all the keys of stat map are true to avoid unnecessary loops
 * @param {Object} statKeys statkeys
 * @return {Boolean} true/false
 */
  checkTruthsOfStatsMap(statKeys) {
    let truthFlag = true
    for(var stat in statKeys) {
      truthFlag = truthFlag && statKeys[stat]
    }
    return truthFlag
  }
}
export default helpers



// WEBPACK FOOTER //
// ../app/lib/transform/parsers/cricket/gamepackage/helpers.js